import { isObject } from "@warrenio/utils/isObject";
import type { JSXElementConstructor, ReactElement, ReactNode } from "react";

/**
 * Extracts the text content from a ReactNode. This function traverses the JSX tree and concatenates the text
 * content of all nodes, optionally using a custom renderer for React elements.
 *
 * It handles common elements like `p`, `div`, `span`, and `br`. Anything else should use a custom renderer.
 *
 * @param renderer - An optional function that takes a ReactElement and returns a string, or `undefined`, which means the
 * default renderer should be used.
 */
export function getReactNodeText(node: ReactNode, renderer?: (e: ReactElement) => string | undefined): string {
    if (node == null) {
        return "";
    }

    switch (typeof node) {
        case "string":
            return node;

        case "number":
            return String(node);

        case "boolean":
            return "";

        case "object": {
            if (Array.isArray(node)) {
                return (node as ReactNode[]).map((e) => getReactNodeText(e, renderer)).join("");
            }

            if ("props" in node && "type" in node) {
                const { props, type } = node;
                if (renderer) {
                    const value = renderer(node);
                    if (value !== undefined) {
                        return value;
                    }
                }

                // Handle common elements
                if (type === "br") {
                    return "\n";
                } else if (isObject(props) && "children" in props) {
                    const text = getReactNodeText(props.children as ReactNode, renderer);
                    if (type === "p" || type === "div") {
                        return `${text}\n`;
                    }
                    return text;
                } else if (type === "div" || type === "span") {
                    // Childless wrapper elements
                    return "";
                }

                console.warn("Unknown JSX element of type %s: %o", type, node);
                return "";
            }
        } // eslint-ignore-line no-fallthrough

        default:
            console.warn("Unhandled JSX node of type %s: %o", typeof node, node);
            return "";
    }
}

export function isElementType<P>(element: ReactElement, type: JSXElementConstructor<P>): element is ReactElement<P> {
    return element.type === type;
}
